/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import type {
  AvatarRecord,
  MicrotaskAssignmentRecord,
  MicrotaskAssignmentStatus,
  MicrotaskRecord,
  MicrotaskStatus,
  TaskRecord,
  TaskStatus,
  WorkerJourneyState,
  WorkerRecord,
} from '@karya/core';
import { err, ok, type AsyncResult } from '@karya/error-values';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { DatabaseInitializationError } from './Error';
import { DatabaseModel } from './DatabaseModel/Model';
import * as WorkerModel from './Worker';

export interface KaryaDB extends DBSchema {
  worker: {
    key: string;
    value: WorkerRecord;
    indexes: { phone_number: string };
  };
  avatar: {
    key: string;
    value: AvatarRecord;
    indexes: {
      worker_id: string;
      project_id: string;
      task_id: string;
      journey_state: WorkerJourneyState;
    };
  };
  task: {
    key: string;
    value: TaskRecord;
    indexes: {
      project_id: string;
      status: TaskStatus;
    };
  };
  microtask: {
    key: string;
    value: MicrotaskRecord;
    indexes: {
      task_id: string;
      status: MicrotaskStatus;
    };
  };
  microtask_assignment: {
    key: string;
    value: MicrotaskAssignmentRecord;
    indexes: {
      task_id: string;
      microtask_id: string;
      worker_id: string;
      avatar_id: string;
      status: MicrotaskAssignmentStatus;
    };
  };
}

export let idb: IDBPDatabase<KaryaDB>;
export let isDatabaseReady = false;

export type Models = {
  basic: DatabaseModel;
  worker: typeof WorkerModel;
};
export let models: Models;

export async function setupDatabase(): AsyncResult<IDBPDatabase<KaryaDB>, Error> {
  if (idb && isDatabaseReady) {
    return ok(idb);
  }

  try {
    // Database setup logic here
    idb = await openDB<KaryaDB>('evals-web-client-db', 1, {
      upgrade(db) {
        // Create object stores and indexes as needed
        if (!db.objectStoreNames.contains('worker')) {
          const workerStore = db.createObjectStore('worker', { keyPath: 'id' });
          workerStore.createIndex('phone_number', 'phone_number');
        }

        if (!db.objectStoreNames.contains('avatar')) {
          const avatarStore = db.createObjectStore('avatar', { keyPath: 'id' });
          avatarStore.createIndex('worker_id', 'worker_id');
          avatarStore.createIndex('project_id', 'project_id');
          avatarStore.createIndex('task_id', 'task_id');
          avatarStore.createIndex('journey_state', 'journey_state');
        }

        if (!db.objectStoreNames.contains('task')) {
          const taskStore = db.createObjectStore('task', { keyPath: 'id' });
          taskStore.createIndex('project_id', 'project_id');
          taskStore.createIndex('status', 'status');
        }

        if (!db.objectStoreNames.contains('microtask')) {
          const microtaskStore = db.createObjectStore('microtask', { keyPath: 'id' });
          microtaskStore.createIndex('task_id', 'task_id');
          microtaskStore.createIndex('status', 'status');
        }

        if (!db.objectStoreNames.contains('microtask_assignment')) {
          const microtaskAssignmentStore = db.createObjectStore('microtask_assignment', { keyPath: 'id' });
          microtaskAssignmentStore.createIndex('task_id', 'task_id');
          microtaskAssignmentStore.createIndex('microtask_id', 'microtask_id');
          microtaskAssignmentStore.createIndex('worker_id', 'worker_id');
          microtaskAssignmentStore.createIndex('avatar_id', 'avatar_id');
          microtaskAssignmentStore.createIndex('status', 'status');
        }
      },
    });

    idb.onclose = () => {
      isDatabaseReady = false;
    };

    configureModels(idb);
    isDatabaseReady = true;
    return ok(idb);
  } catch (error) {
    console.error('Error setting up database:', error);
    return err(new DatabaseInitializationError(error instanceof Error ? error : undefined));
  }
}

export function configureModels(dbInstance: IDBPDatabase<KaryaDB>) {
  models = {
    basic: new DatabaseModel(dbInstance),
    worker: WorkerModel,
  };
}
